import { CheckCircleIcon, ArrowRightIcon, SparklesIcon, ArrowUpIcon } from "@heroicons/react/24/solid";
import { motion } from "framer-motion";
import React, { useState, useEffect } from 'react';

const INFLUENTIAL_FACTORS = [
  {
    title: "Sleep",
    description: "Getting adequate and quality sleep for optimal health",
    questions: [
      "I consistently get 7-9 hours of sleep each night.",
    ],
    action: "Establish a consistent sleep schedule and create a relaxing bedtime routine"
  },
  {
    title: "Diet",
    description: "Maintaining balanced and consistent nutrition",
    questions: [
      "I eat regular, balanced meals throughout the day.",
    ],
    action: "Plan meals ahead and include a variety of nutritious foods in your diet"
  },
  {
    title: "Hydration",
    description: "Maintaining proper water intake for optimal body function",
    questions: [
      "I drink at least 64 ounces of water each day.",
    ],
    action: "Keep a water bottle with you and set reminders to drink water regularly"
  },
  {
    title: "Exercise",
    description: "Engaging in regular physical activity",
    questions: [
      "I exercise for at least 30 minutes most days of the week.",
    ],
    action: "Schedule regular exercise sessions and mix different types of activities"
  }
];

export default function PhysicalHealth() {
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show button when user is near bottom (within 100px)
      setShowScrollButton(documentHeight - scrollPosition < 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8">
      {/* Hero Section */}
      <div className="lg:col-span-12">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-2xl" />
          <div className="relative p-4 lg:p-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center space-x-2 bg-emerald-100 px-3 lg:px-4 py-2 rounded-full mb-4"
            >
              <SparklesIcon className="h-4 w-4 lg:h-5 lg:w-5 text-emerald-600" />
              <span className="text-emerald-600 font-medium text-sm lg:text-base">Domain Deep Dive</span>
            </motion.div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 lg:mb-4">Physical Health</h1>
            <p className="text-base lg:text-lg text-gray-600">
              Physical health is the foundation of overall well-being and performance. It encompasses not just exercise and nutrition, but also sleep, stress management,
              and regular health check-ups. Taking care of your physical health helps you maintain energy, focus, and resilience in both work and personal life.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="lg:col-span-8 space-y-4 lg:space-y-8">
        {/* Action Steps */}
        <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4">How to Improve Physical Health</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
            {[
              {
                title: "Prioritize Sleep",
                description: "Aim for 7-9 hours of quality sleep each night to support recovery and cognitive function."
              },
              {
                title: "Move Regularly",
                description: "Incorporate physical activity into your daily routine, even if it's just short walks or stretching."
              },
              {
                title: "Eat Mindfully",
                description: "Choose nourishing foods that provide sustained energy and support your body's needs."
              },
              {
                title: "Manage Stress",
                description: "Practice stress-reduction techniques like deep breathing, meditation, or time in nature."
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-x-3 p-3 rounded-xl hover:bg-emerald-50 transition-colors"
              >
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-emerald-600 text-sm font-semibold">{index + 1}</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{step.title}</h3>
                  <p className="mt-1 text-gray-600 text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Reflection Questions */}
        <div className="bg-emerald-50 rounded-2xl p-4 lg:p-6">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4">Reflection Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
            {[
              "How do I prioritize my physical health?",
              "What barriers prevent me from being more active?",
              "How does my sleep quality affect my work?",
              "What healthy habits would I like to develop?",
              "How do I manage stress physically?",
              "What small changes could I make today?"
            ].map((question, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-3 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-x-2">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-emerald-600 text-xs">?</span>
                    </div>
                  </div>
                  <p className="text-gray-900 text-sm">{question}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Sidebar Content */}
      <div className="lg:col-span-4 space-y-4 lg:space-y-8">
        {/* Self-Assessment */}
        <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4 lg:mb-6">Influential Factors</h2>
          <div className="space-y-6 lg:space-y-8">
            {INFLUENTIAL_FACTORS.map((factor, index) => (
              <div key={index} className="space-y-3 lg:space-y-4">
                <div>
                  <h3 className="text-base lg:text-lg font-medium text-gray-900">{factor.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{factor.description}</p>
                </div>
                
                <div className="space-y-3">
                  {factor.questions.map((question, qIndex) => (
                    <div key={qIndex} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0" />
                      <p className="text-sm text-gray-700">{question}</p>
                    </div>
                  ))}
                </div>

                <div className="flex items-start gap-2 bg-emerald-50 p-3 rounded-lg">
                  <SparklesIcon className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-emerald-900">Action Item</p>
                    <p className="text-sm text-emerald-700 mt-1">{factor.action}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: showScrollButton ? 1 : 0, y: showScrollButton ? 0 : 20 }}
        transition={{ duration: 0.3 }}
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 bg-emerald-600 text-white p-3 rounded-full shadow-lg hover:bg-emerald-700 transition-colors ${
          showScrollButton ? 'block' : 'hidden'
        }`}
      >
        <ArrowUpIcon className="h-6 w-6" />
      </motion.button>
    </div>
  );
}
